import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo } from 'react';
import { Breadcrumb } from '../../../other/Breadcrum';
import { AnalyticsCard } from '../../../other/AnalyticsCard';
import { SkeletonLoader } from '@widget/loaders';
import classNames from 'classnames';
/**
 * Type guard to check if data is ObjectDetailsPanelHeaderProps
 */
export function isObjectDetailsPanelHeaderProps(data) {
    return data && typeof data === 'object' && 'title' in data && !('fields' in data);
}
const ObjectDetailsPanelHeaderBase = ({ item, breadcrumb, title, status, subtitle, analytics, backgroundImage, loading, className }) => {
    // Helper function to resolve potentially-function props
    const resolveValue = (value, defaultValue) => {
        if (value === undefined)
            return defaultValue;
        if (typeof value === 'function' && item) {
            return value(item, loading);
        }
        return value;
    };
    // Resolve all props
    const resolvedBreadcrumb = resolveValue(breadcrumb);
    const resolvedTitle = resolveValue(title);
    const resolvedStatus = resolveValue(status);
    const resolvedSubtitle = resolveValue(subtitle);
    const resolvedAnalytics = resolveValue(analytics);
    const resolvedBackgroundImage = resolveValue(backgroundImage);
    return (_jsxs("div", { className: classNames("uxpcore_obsdph__container", className), style: resolvedBackgroundImage ? { backgroundImage: `url(${resolvedBackgroundImage})` } : undefined, children: [resolvedBreadcrumb && resolvedBreadcrumb.length > 0 && (_jsx("div", { className: "uxpcore_obsdph__breadcrumb", children: _jsx(Breadcrumb, { items: resolvedBreadcrumb }) })), _jsxs("div", { className: "uxpcore_obsdph__content", children: [_jsxs("div", { className: "uxpcore_obsdph__left", children: [_jsxs("div", { className: "uxpcore_obsdph__title-wrapper", children: [_jsx("div", { className: "uxpcore_obsdph__title", children: loading ? (_jsx(SkeletonLoader, { width: "8rem", height: "1rem" })) : (resolvedTitle) }), resolvedStatus && (_jsx("div", { className: "uxpcore_obsdph__status", children: loading ? (_jsx(SkeletonLoader, { width: "5rem", height: "2.5rem", additionalStyle: { borderRadius: '2rem' } })) : (resolvedStatus) }))] }), resolvedSubtitle && (_jsx("div", { className: "uxpcore_obsdph__subtitle", children: loading ? (_jsx(SkeletonLoader, { width: "10rem", height: "0.875rem" })) : (resolvedSubtitle) }))] }), resolvedAnalytics && resolvedAnalytics.length > 0 && (_jsx("div", { className: "uxpcore_obsdph__right", children: _jsx("div", { className: "uxpcore_obsdph__analytics", children: resolvedAnalytics.map((analytic, index) => (_jsx(AnalyticsCard, Object.assign({}, analytic, { loading: loading, small: true }), index))) }) }))] })] }));
};
/**
 * @export
 * A header component for ObjectDetailsPanel with breadcrumb, title, subtitle, and analytics cards.
 *
 * @example
 * ```tsx
 * <ObjectDetailsPanelHeader
 *   breadcrumb={[
 *     { label: 'Home', link: '/' },
 *     { label: 'Locations', link: '/locations' },
 *     { label: 'Singapore' }
 *   ]}
 *   title="Singapore Office"
 *   subtitle={<Chip icon="fas circle" label="Active" />}
 *   analytics={[
 *     { icon: 'fas building', value: 5, label: 'Buildings' },
 *     { icon: 'fas layer-group', value: 12, label: 'Floors' }
 *   ]}
 *   backgroundImage="/images/office.jpg"
 * />
 * ```
 *
 * @example
 * ```tsx
 * <ObjectDetailsPanelHeader
 *   title="Simple Header"
 *   subtitle="No breadcrumb or analytics"
 * />
 * ```
 *
 * @partOf ObjectDetailsPanel
 */
export const ObjectDetailsPanelHeader = memo(ObjectDetailsPanelHeaderBase);
